import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Boxes,
  Clock3,
  Command,
  Ellipsis,
  FileText,
  Map as MapIcon,
  PanelLeftOpen,
  Plus,
  Radar,
  Route,
  Search,
  SlidersHorizontal,
  Sparkles,
  Tag,
  Waypoints,
  X,
} from 'lucide-react';
import { Button, Dialog, DialogBody, DialogHeader, ENTITY_TONE, EmptyState, IconButton, Menu, Spinner } from '../../../components/ui';
import { useTaxonomyColors } from '../../../hooks/useTaxonomyColors';
import type { TaxonomyItem } from '../../../services/taxonomiesService';
import { pathsBetween } from '../graphAnalytics';
import { acceptLead, discoverConnections, dismissLead } from '../intelligenceApi';
import type { ConnectionLead, DiscoverRequest, LinkKind, NetworkLink, NetworkNode, RelationKind } from '../types';
import { useIntelligenceStream } from '../useIntelligenceStream';
import { AskPanel, AskPanelHandle } from '../components/AskPanel';
import { BriefingRail } from './BriefingRail';
import { Dossier } from './Dossier';
import { EntityPicker } from './EntityPicker';
import { FiltersPanel } from './FiltersPanel';
import { MobileSheet, SheetSnap } from './MobileSheet';
import { NetworkCanvas, NetworkCanvasHandle } from './NetworkCanvas';
import { AddToDirectoryDialog } from './AddToDirectoryDialog';
import { CreateLinkDialog, EditLinkDialog } from './RelationshipDialogs';
import { asFlag, asList, asOneOf, useUrlState, useUrlWriter } from '../../../hooks/useUrlState';
import { TimeScrubber } from './TimeScrubber';
import {
  DEFAULT_FILTERS,
  LENS_LABEL,
  Lens,
  NetworkFilters,
  NetworkView,
  activeFilterCount,
  analyzeView,
  buildNetworkContext,
  buildSuggestions,
  clusterNames,
  drawableLeads,
  filterGraph,
  intervalsFor,
  linksActiveAt,
  modularityLabel,
  monthBucket,
  roleLine,
  timeExtent,
  timeHistogram,
  vulnerabilities,
} from './networkModel';
import { NodeSwatch, TieSwatch, useMediaQuery, useReducedMotion } from './ui';
import { useNetworkData } from './useNetworkData';

/*
 * Full-bleed workspace: cancels the dashboard's main padding and fills the space between the
 * header (56px phones, 64px desktop, plus its hairline) and the phone tab bar.
 */
const WORKSPACE_CLASS =
  'relative -mx-4 -mt-4 flex h-[calc(100dvh_-_57px_-_env(safe-area-inset-top,0px)_-_var(--app-tabbar-h))] flex-col overflow-hidden bg-white font-ui text-ink antialiased sm:-mx-6 sm:-mt-6 lg:h-[calc(100dvh_-_65px_-_env(safe-area-inset-top,0px))]';
const WORKSPACE_STYLE: React.CSSProperties = { marginBottom: 'calc(-1 * (var(--app-tabbar-h, 0px) + 1.5rem))' };
const FLOATING = 'bg-white shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]';

const WORKSPACE_CSS = `
@keyframes nw-in-right { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
@keyframes nw-in-left { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: none; } }
.nw-in-right { animation: nw-in-right 280ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.nw-in-left { animation: nw-in-left 280ms cubic-bezier(0.16, 1, 0.3, 1) both; }
@media (prefers-reduced-motion: reduce) { .nw-in-right, .nw-in-left { animation: none; } }
`;

const EMPTY_VIEW: NetworkView = { nodes: [], links: [], nodesById: new Map() };

type MobileTab = 'briefing' | 'dossier' | 'ask';

const LENS_ICON: Record<Lens, React.ElementType> = { map: MapIcon, focus: Radar, clusters: Boxes };

/*
 * What the analyst is looking at belongs in the address bar: the lens, the
 * filters, who is selected. Leaving for a profile and coming back should find
 * the same view, and a link should carry it to a colleague.
 *
 * Filters are written as what has been turned OFF, so a plain URL means the
 * ordinary view and only a deliberate narrowing shows up in the link.
 */
const LENSES = ['map', 'focus', 'clusters'] as const;
const KIND_KEYS: LinkKind[] = ['employment', 'hierarchy', 'co_affiliation', 'relationship'];
const TYPE_KEYS: RelationKind[] = ['os', 'oo', 'ss'];
const LENS = asOneOf(LENSES);
const ENTITIES = asOneOf(['all', 'stakeholder', 'organization'] as const);
const PANEL = asOneOf(['dossier', 'ask'] as const);
const KINDS_OFF = asList<LinkKind>(KIND_KEYS);
const TYPES_OFF = asList<RelationKind>(TYPE_KEYS);
const NONE_OFF: never[] = [];

export const NetworkWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const reducedMotion = useReducedMotion();
  const taxonomy = useTaxonomyColors();
  const taxRef = useRef(taxonomy);
  taxRef.current = taxonomy;

  /* ---------------- data ---------------- */
  const writeUrl = useUrlWriter();
  const [entities] = useUrlState<NetworkFilters['entities']>('entities', 'all', ENTITIES);
  const [kindsOff] = useUrlState<LinkKind[]>('kindsOff', NONE_OFF, KINDS_OFF);
  const [typesOff] = useUrlState<RelationKind[]>('typesOff', NONE_OFF, TYPES_OFF);
  const [includePast] = useUrlState('past', false, asFlag);
  const [hideIsolated] = useUrlState('hideIsolated', false, asFlag);
  const filters = useMemo<NetworkFilters>(() => {
    const on = <T extends string>(keys: T[], off: T[]) => {
      const map = {} as Record<T, boolean>;
      for (const key of keys) map[key] = !off.includes(key);
      return map;
    };
    return {
      entities,
      kinds: on(KIND_KEYS, kindsOff),
      types: on(TYPE_KEYS, typesOff),
      includePast,
      hideIsolated,
    };
  }, [entities, kindsOff, typesOff, includePast, hideIsolated]);
  const setFilters = useCallback(
    (next: NetworkFilters) => {
      writeUrl({
        entities: next.entities === 'all' ? null : next.entities,
        kindsOff: KIND_KEYS.filter((key) => !next.kinds[key]).join(',') || null,
        typesOff: TYPE_KEYS.filter((key) => !next.types[key]).join(',') || null,
        past: next.includePast ? '1' : null,
        hideIsolated: next.hideIsolated ? '1' : null,
      });
    },
    [writeUrl],
  );
  const [timeOn, setTimeOn] = useUrlState('timeOn', false, asFlag);
  const data = useNetworkData();
  const { graph, leads, setLeads } = data;

  const view = useMemo(() => (graph ? filterGraph(graph, filters, { keepPast: timeOn }) : EMPTY_VIEW), [graph, filters, timeOn]);
  const intervals = useMemo(() => intervalsFor(view.links, view.nodesById), [view]);
  const extent = useMemo(() => timeExtent(intervals), [intervals]);
  const hasDates = useMemo(
    () => !!graph && (graph.links.some((l) => l.startDate || l.endDate) || graph.nodes.some((n) => n.roles?.some((r) => r.startDate || r.endDate))),
    [graph],
  );
  const histogram = useMemo(() => (extent ? timeHistogram(intervals, extent) : { starts: [], ends: [] }), [intervals, extent]);

  const [time, setTime] = useState(() => Date.now());
  const [playing, setPlaying] = useState(false);
  const deferredTime = useDeferredValue(time);
  const bucket = monthBucket(deferredTime);
  const activeLinks = useMemo(
    () => (timeOn ? linksActiveAt(view.links, intervals, deferredTime) : view.links),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, intervals, timeOn, bucket],
  );

  const analysis = useMemo(() => analyzeView(view.nodes, activeLinks), [view.nodes, activeLinks]);
  const names = useMemo(() => clusterNames(analysis, view.nodesById), [analysis, view.nodesById]);
  const vulns = useMemo(() => vulnerabilities(analysis, view.nodes), [analysis, view.nodes]);

  /* ---------------- interaction state ---------------- */
  const [lens, setLens] = useUrlState<Lens>('lens', 'map', LENS);
  const [sel, setSel] = useUrlState('sel', '');
  const [ego, setEgo] = useUrlState('ego', '');
  const [compare, setCompare] = useUrlState('compare', '');
  const selectedId = sel || null;
  const lastEgo = ego || null;
  const compareId = compare || null;
  const setSelectedId = useCallback((id: string | null) => setSel(id ?? ''), [setSel]);
  const setLastEgo = useCallback((id: string | null) => setEgo(id ?? ''), [setEgo]);
  const setCompareId = useCallback((id: string | null) => setCompare(id ?? ''), [setCompare]);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [highlightIds, setHighlightIds] = useState<string[]>([]);
  const [highlightCluster, setHighlightCluster] = useState<number | null>(null);
  const [pathEnds, setPathEnds] = useState<{ from: string; to: string } | null>(null);
  const [activePath, setActivePath] = useState(0);
  const [pickMode, setPickMode] = useState<'path' | 'link' | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [linkPair, setLinkPair] = useState<[string, string] | null>(null);
  const [editLink, setEditLink] = useState<NetworkLink | null>(null);
  const [showLabels, setShowLabels] = useUrlState('labels', true, asFlag);
  const [leftOpen, setLeftOpen] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [rightTab, setRightTab] = useUrlState<'dossier' | 'ask'>('panel', 'dossier', PANEL);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('briefing');
  const [sheetSnap, setSheetSnap] = useState<SheetSnap>('peek');
  const [leadFocusId, setLeadFocusId] = useState<string | null>(null);
  /** The lead whose counterpart is missing from the directory, while it is being added */
  const [adding, setAdding] = useState<ConnectionLead | null>(null);
  const [solidifying, setSolidifying] = useState<Map<string, number>>(() => new Map());
  const [freshLeadIds, setFreshLeadIds] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [mainHeight, setMainHeight] = useState(600);

  const canvasRef = useRef<NetworkCanvasHandle>(null);
  const askRef = useRef<AskPanelHandle>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const pendingQuestion = useRef<string | null>(null);

  const rightOpen = isDesktop && (askOpen || !!(selectedId && view.nodesById.has(selectedId)));

  // Keep the map usable on narrower screens: the briefing steps aside while a dossier is open
  const workspaceRef = useRef<HTMLDivElement>(null);
  const autoCollapsed = useRef(false);
  useEffect(() => {
    const width = workspaceRef.current?.getBoundingClientRect().width || 0;
    if (rightOpen && leftOpen && width && width - 320 - 360 < 560) {
      autoCollapsed.current = true;
      setLeftOpen(false);
    } else if (!rightOpen && autoCollapsed.current) {
      autoCollapsed.current = false;
      setLeftOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightOpen]);

  const paths = useMemo(() => (pathEnds ? pathsBetween(analysis, pathEnds.from, pathEnds.to, 3) : []), [analysis, pathEnds]);
  const pathsRef = useRef(paths);
  pathsRef.current = paths;
  // Frame a path as soon as it is drawn (and when switching between alternatives)
  const pathKey = paths[activePath]?.join('>') || '';
  useEffect(() => {
    if (!pathKey) return;
    const timer = window.setTimeout(() => canvasRef.current?.fit(pathsRef.current.flat()), 60);
    return () => window.clearTimeout(timer);
  }, [pathKey]);
  const selectedNode = selectedId ? view.nodesById.get(selectedId) || null : null;
  const egoId = selectedId || lastEgo || analysis.ranked.influence[0] || null;

  /* ---------------- helpers ---------------- */
  const lineOf = useCallback(
    (n: NetworkNode) =>
      roleLine(n, (domain, key) => taxRef.current.labelOf(domain, key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taxonomy.loaded],
  );
  const entityColor = useCallback((id: string) => {
    const n = graph?.nodes.find((x) => x.id === id);
    return n ? ENTITY_TONE[n.type].hex : undefined;
  }, [graph]);
  const categoryPill = useCallback((n: NetworkNode) => {
    const t = taxRef.current;
    if (n.type === 'stakeholder' && n.category) {
      return { label: t.labelOf('stakeholder_category', n.category) || n.category, color: t.colorOf('stakeholder_category', n.category) };
    }
    if (n.type === 'organization' && n.orgType) {
      return { label: t.labelOf('organization_type', n.orgType) || n.orgType, color: t.colorOf('organization_type', n.orgType) };
    }
    return null;
  }, []);
  const relationshipColor = useCallback((category?: string | null) => taxRef.current.colorOf('relationship_category', category), []);

  const flash = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice((m) => (m === message ? null : m)), 2600);
  }, []);

  useEffect(() => {
    const el = mainRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setMainHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDesktop]);

  /* ---------------- selection & navigation ---------------- */
  const select = useCallback(
    (id: string | null, fly = true) => {
      setSelectedId(id);
      if (!id) return;
      setLastEgo(id);
      if (!pathsRef.current.some((p) => p.includes(id))) setPathEnds(null);
      if (compareId === id) setCompareId(null);
      setRightTab('dossier');
      // after the render, so the canvas knows how much the phone sheet covers
      if (fly && lens !== 'focus') window.setTimeout(() => canvasRef.current?.flyTo(id), 0);
      if (!isDesktop) {
        setMobileTab('dossier');
        setSheetSnap((s) => (s === 'peek' ? 'half' : s));
      }
    },
    [lens, isDesktop, compareId, setSelectedId, setLastEgo, setCompareId, setRightTab],
  );

  // Arriving from a profile's "Open in Network": open that entity in the focus
  // lens, which is the same question the profile was asking — everything
  // around this one — then drop the parameter so a reload does not fight the
  // reader's own selection
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || !graph) return;
    arrived.current = true;
    const wanted = new URLSearchParams(window.location.search).get('focus');
    if (!wanted || !graph.nodes.some((n) => n.id === wanted)) return;
    // Rewrite it as the view it means, so the rest of the page's state and
    // the back button agree about where we are
    writeUrl({ focus: null, lens: 'focus', sel: wanted, ego: wanted, panel: 'dossier' });
  }, [graph, select, writeUrl]);

  const selectFromPanel = useCallback(
    (id: string) => {
      if (!view.nodesById.has(id) || !analysis.metrics.has(id)) {
        if (graph?.nodes.some((n) => n.id === id)) flash('Hidden by the current filters');
        return;
      }
      select(id);
    },
    [view, analysis, graph, select, flash],
  );

  const openProfile = useCallback(
    (id: string) => {
      const n = graph?.nodes.find((x) => x.id === id);
      if (n) navigate(n.type === 'stakeholder' ? `/stakeholders/${id}` : `/organizations/${id}`);
    },
    [graph, navigate],
  );

  const handleNodeClick = useCallback(
    (id: string) => {
      if (pickMode === 'path') {
        if (!picked.length) setPicked([id]);
        else if (picked[0] !== id) {
          const from = picked[0];
          setPathEnds({ from, to: id });
          setActivePath(0);
          setPickMode(null);
          setPicked([]);
          select(from, false);
          if (!pathsBetween(analysis, from, id, 1).length) flash('No connection between them in this view');
        }
        return;
      }
      if (pickMode === 'link') {
        if (!picked.length) setPicked([id]);
        else if (picked[0] !== id) {
          setLinkPair([picked[0], id]);
          setPickMode(null);
          setPicked([]);
        }
        return;
      }
      select(id === selectedId ? null : id);
    },
    [pickMode, picked, select, selectedId, analysis, flash],
  );

  const clearOne = useCallback(() => {
    if (pickMode) {
      setPickMode(null);
      setPicked([]);
    } else if (searchOpen) setSearchOpen(false);
    else if (filtersOpen) setFiltersOpen(false);
    else if (askOpen && isDesktop) setAskOpen(false);
    else if (pathEnds) setPathEnds(null);
    else if (compareId) setCompareId(null);
    else if (selectedId) setSelectedId(null);
    else if (highlightIds.length) setHighlightIds([]);
  }, [pickMode, searchOpen, filtersOpen, askOpen, isDesktop, pathEnds, compareId, selectedId, highlightIds, setCompareId, setSelectedId]);

  const startPick = (mode: 'path' | 'link') => {
    if (pickMode === mode) {
      setPickMode(null);
      setPicked([]);
      return;
    }
    setPickMode(mode);
    setPicked(selectedId ? [selectedId] : []);
    if (mode === 'path') setPathEnds(null);
    if (!isDesktop) setSheetSnap('peek');
  };

  const changeLens = useCallback(
    (next: Lens) => {
      setLens(next);
      if (next === 'focus' && !selectedId && egoId) setLastEgo(egoId);
    },
    [selectedId, egoId, setLens, setLastEgo],
  );

  const focusCluster = useCallback(
    (community: number) => {
      setLens('clusters');
      setHighlightCluster(community);
      window.setTimeout(() => canvasRef.current?.fitCluster(community), lens === 'clusters' ? 0 : 950);
      window.setTimeout(() => setHighlightCluster((c) => (c === community ? null : c)), 2600);
      if (!isDesktop) setSheetSnap('peek');
    },
    [lens, isDesktop, setLens],
  );

  /* ---------------- AI ---------------- */
  const buildContext = useCallback(
    () =>
      buildNetworkContext({
        view,
        links: activeLinks,
        analysis,
        names,
        vulns,
        filters,
        lens,
        selectedId,
        compareId,
        time: timeOn ? time : null,
        paths,
      }),
    [view, activeLinks, analysis, names, vulns, filters, lens, selectedId, compareId, timeOn, time, paths],
  );

  const suggestions = useMemo(
    () => buildSuggestions({ analysis, nodesById: view.nodesById, names, vulns, selectedId }),
    [analysis, view.nodesById, names, vulns, selectedId],
  );

  const openAsk = useCallback(
    (question?: string) => {
      pendingQuestion.current = question ?? '';
      if (isDesktop) {
        setAskOpen(true);
        setRightTab('ask');
      } else {
        setMobileTab('ask');
        setSheetSnap('full');
      }
    },
    [isDesktop, setRightTab],
  );

  // Hand a pending question (or focus) to the panel once it is mounted
  useEffect(() => {
    if (pendingQuestion.current === null) return;
    const timer = window.setTimeout(() => {
      if (!askRef.current || pendingQuestion.current === null) return;
      const q = pendingQuestion.current;
      pendingQuestion.current = null;
      if (q) askRef.current.ask(q);
      else askRef.current.focus();
    }, 60);
    return () => window.clearTimeout(timer);
  });

  const aiCallbacks = useMemo(
    () => ({
      onEntity: (id: string) => selectFromPanel(id),
      onEntityHover: (id: string | null) => setHoverId(id),
      entityColor,
      onReferences: (ids: string[]) => setHighlightIds(ids),
    }),
    [selectFromPanel, entityColor],
  );

  const discover = useIntelligenceStream<DiscoverRequest, { leads: ConnectionLead[] }>(discoverConnections);
  const [discoverSubject, setDiscoverSubject] = useState<string | null>(null);
  const onDiscover = useCallback(
    (focus: string) => {
      if (!selectedNode) return;
      setDiscoverSubject(selectedNode.id);
      discover.start({ entityId: selectedNode.id, entityType: selectedNode.type, focus: focus.trim() || undefined });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedNode, discover.start],
  );
  useEffect(() => {
    if (discover.status !== 'done' || !discover.result?.leads) return;
    const found = discover.result.leads.filter((l) => l.kind === 'connection');
    setLeads((current) => [...found, ...current.filter((l) => !found.some((f) => f.id === l.id))]);
    setFreshLeadIds(new Set(found.map((l) => l.id)));
    if (found.length) flash(`${found.length} ${found.length === 1 ? 'lead' : 'leads'} found`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discover.status, discover.result]);

  const onAccept = useCallback(
    async (lead: ConnectionLead) => {
      await acceptLead(lead.id);
      setSolidifying((m) => new Map(m).set(lead.id, performance.now()));
      flash('Relationship created');
      window.setTimeout(() => {
        setLeads((ls) => ls.filter((l) => l.id !== lead.id));
        setSolidifying((m) => {
          const next = new Map(m);
          next.delete(lead.id);
          return next;
        });
      }, 1100);
      data.reload(true);
    },
    [data, flash, setLeads],
  );

  const onDismiss = useCallback(
    async (lead: ConnectionLead) => {
      await dismissLead(lead.id);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      if (leadFocusId === lead.id) setLeadFocusId(null);
    },
    [setLeads, leadFocusId],
  );

  const visibleIds = useMemo(() => new Set(view.nodes.map((n) => n.id)), [view.nodes]);
  const canvasLeads = useMemo(() => {
    const pending = drawableLeads(leads, visibleIds);
    const accepted = leads.filter((l) => solidifying.has(l.id) && l.targetId && visibleIds.has(l.subjectId) && visibleIds.has(l.targetId));
    return [...pending, ...accepted.filter((a) => !pending.includes(a))];
  }, [leads, visibleIds, solidifying]);

  /* ---------------- time ---------------- */
  const toggleTime = () => {
    if (timeOn) {
      setTimeOn(false);
      setPlaying(false);
      return;
    }
    setTime(Date.now());
    setTimeOn(true);
  };
  useEffect(() => {
    if (timeOn && extent && (time < extent.min || time > extent.max)) setTime(extent.max);
  }, [timeOn, extent, time]);

  /* ---------------- keyboard ---------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openAsk();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if (e.key === 'Escape') {
        if (typing) return;
        clearOne();
        return;
      }
      if (typing) return;
      if (e.key === '/') {
        e.preventDefault();
        if (!isDesktop) setSearchOpen(true);
        window.setTimeout(() => searchRef.current?.focus(), 30);
      } else if (e.key === 'f' || e.key === 'F') canvasRef.current?.fit();
      else if (e.key === '1') changeLens('map');
      else if (e.key === '2') changeLens('focus');
      else if (e.key === '3') changeLens('clusters');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openAsk, clearOne, changeLens, isDesktop]);

  // Close the filters popover on outside press
  useEffect(() => {
    if (!filtersOpen || !isDesktop) return;
    const onDown = (e: MouseEvent) => {
      if (!filtersRef.current?.contains(e.target as Node)) setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filtersOpen, isDesktop]);

  /* ---------------- render pieces ---------------- */
  const loading = data.loading && !graph;
  const clusterCount = analysis.communities.length;
  const meta = `${view.nodes.length.toLocaleString()} entities · ${activeLinks.length.toLocaleString()} ties · ${clusterCount} ${clusterCount === 1 ? 'cluster' : 'clusters'}`;
  const filterCount = activeFilterCount(filters);
  const categories = taxonomy.items('relationship_category') as TaxonomyItem[];

  const lensControl = (compact: boolean) => (
    <div role="tablist" aria-label="Lens" className={`inline-flex items-center gap-0.5 rounded-full bg-neutral-100 p-0.5 ${compact ? FLOATING + ' !bg-white' : ''}`}>
      {(['map', 'focus', 'clusters'] as Lens[]).map((l, i) => {
        const Icon = LENS_ICON[l];
        const active = lens === l;
        return (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={active}
            title={`${LENS_LABEL[l]} (${i + 1})`}
            onClick={() => changeLens(l)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
              active
                ? compact
                  ? 'bg-neutral-100 font-medium text-ink'
                  : 'bg-white font-medium text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_1px_rgba(0,0,0,0.04)]'
                : 'text-neutral-500 hover:text-ink'
            }`}
          >
            <Icon className={`h-3.5 w-3.5 ${active ? 'text-primary-600' : ''}`} strokeWidth={1.75} />
            {LENS_LABEL[l]}
          </button>
        );
      })}
    </div>
  );

  const toolButton = (label: string, icon: React.ReactNode, onClick: () => void, active?: boolean, disabled?: boolean, badge?: number) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:cursor-not-allowed disabled:opacity-35 ${
        active ? 'bg-primary-50 text-primary-600' : 'text-neutral-500 hover:bg-neutral-100 hover:text-ink'
      }`}
    >
      {icon}
      {!!badge && (
        <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-ink px-1 text-[10px] tabular-nums text-white">
          {badge}
        </span>
      )}
    </button>
  );

  const filtersPanel = graph && <FiltersPanel filters={filters} onChange={setFilters} links={graph.links} timeOn={timeOn} />;

  const dossier = selectedNode && (
    <Dossier
      node={selectedNode}
      view={view}
      links={activeLinks}
      allLinks={graph?.links}
      onShowPast={filters.includePast || timeOn ? undefined : () => setFilters({ ...filters, includePast: true })}
      analysis={analysis}
      names={names}
      vulns={vulns}
      lineOf={lineOf}
      categoryPill={categoryPill}
      relationshipColor={relationshipColor}
      pathTargetId={pathEnds && pathEnds.from === selectedNode.id ? pathEnds.to : null}
      paths={pathEnds && pathEnds.from === selectedNode.id ? paths : []}
      activePath={activePath}
      onPathTarget={(to) => {
        setActivePath(0);
        setPathEnds(to ? { from: selectedNode.id, to } : null);
      }}
      onActivePath={setActivePath}
      compareId={compareId}
      onCompare={setCompareId}
      leadsAvailable={data.leadsAvailable}
      leads={leads.filter((l) => l.status === 'pending' && !solidifying.has(l.id))}
      freshLeadIds={freshLeadIds}
      discover={{ ...discover, subjectId: discoverSubject }}
      onDiscover={onDiscover}
      onStopDiscover={discover.stop}
      onAccept={onAccept}
      onDismiss={onDismiss}
      onAddToDirectory={setAdding}
      leadFocusId={leadFocusId}
      onLeadFocus={setLeadFocusId}
      onSelect={selectFromPanel}
      onHover={setHoverId}
      onClose={() => select(null)}
      onOpenProfile={() => openProfile(selectedNode.id)}
      onLink={() => {
        setPickMode('link');
        setPicked([selectedNode.id]);
        if (!isDesktop) setSheetSnap('peek');
      }}
      onEditLink={setEditLink}
      onFocusLens={() => changeLens('focus')}
      onFocusCluster={focusCluster}
      hideClose={!isDesktop}
    />
  );

  const askPanel = (onClose?: () => void) => (
    <AskPanel
      ref={askRef}
      domain="network"
      title="Ask the network"
      placeholder="Ask about people, organizations, access…"
      suggestions={suggestions}
      buildContext={buildContext}
      focusIds={[selectedId, compareId, ...(pathEnds ? [pathEnds.to] : [])].filter(Boolean) as string[]}
      scopeLabel={selectedNode ? `Focused on ${selectedNode.label}` : 'Whole view'}
      onEntity={aiCallbacks.onEntity}
      onEntityHover={aiCallbacks.onEntityHover}
      entityColor={entityColor}
      onReferences={aiCallbacks.onReferences}
      onShowReferences={(ids) => {
        setHighlightIds(ids);
        canvasRef.current?.fit(ids.filter((id) => view.nodesById.has(id)));
        if (!isDesktop) setSheetSnap('peek');
      }}
      onClose={onClose}
      className="h-full"
    />
  );

  const briefing = (collapsible: boolean) => (
    <BriefingRail
      view={view}
      tieCount={activeLinks.length}
      analysis={analysis}
      names={names}
      vulns={vulns}
      selectedId={selectedId}
      lineOf={lineOf}
      onSelect={selectFromPanel}
      onHover={setHoverId}
      onHoverCluster={setHighlightCluster}
      onFocusCluster={focusCluster}
      onShowTie={(a, b) => {
        select(a, false);
        setPathEnds({ from: a, to: b });
        setActivePath(0);
        canvasRef.current?.fit([a, b]);
      }}
      buildContext={buildContext}
      ai={aiCallbacks}
      onCollapse={
        collapsible
          ? () => {
              autoCollapsed.current = false;
              setLeftOpen(false);
            }
          : undefined
      }
    />
  );

  const pickBanner = pickMode && (
    <div role="status" className={`pointer-events-auto flex min-w-0 max-w-full animate-fade-up items-center gap-2 rounded-full py-1 pl-3.5 pr-1 ${FLOATING}`}>
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-600" />
      <span className="truncate text-[13px] text-ink">
        {pickMode === 'path'
          ? picked.length
            ? `Path from ${view.nodesById.get(picked[0])?.label} to…`
            : 'Pick the start'
          : picked.length
            ? `Link ${view.nodesById.get(picked[0])?.label} to…`
            : 'Pick the first entity'}
      </span>
      <Button
        size="xs"
        variant="ghost"
        onClick={() => {
          setPickMode(null);
          setPicked([]);
        }}
      >
        Cancel
      </Button>
    </div>
  );

  const pathChip = paths.length > 0 && pathEnds && !pickMode && (
    <div className={`pointer-events-auto flex min-w-0 max-w-full animate-fade-up items-center gap-2 rounded-full py-1 pl-3 pr-1 ${FLOATING}`}>
      <Route className="h-3.5 w-3.5 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
      <span className="min-w-0 truncate text-[12.5px] text-ink">
        {view.nodesById.get(pathEnds.from)?.label} <span className="text-neutral-400">→</span> {view.nodesById.get(pathEnds.to)?.label}
      </span>
      <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-500">
        {paths[activePath]?.length - 1} {paths[activePath]?.length === 2 ? 'hop' : 'hops'}
      </span>
      {paths.length > 1 && (
        <span className="flex flex-shrink-0 items-center gap-0.5">
          {paths.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Path ${i + 1}`}
              onClick={() => setActivePath(i)}
              className={`h-6 w-6 rounded-full text-[11px] tabular-nums transition-colors ${i === activePath ? 'bg-primary-50 text-primary-700' : 'text-neutral-500 hover:bg-neutral-100'}`}
            >
              {i + 1}
            </button>
          ))}
        </span>
      )}
      <IconButton label="Clear path" size="xs" onClick={() => setPathEnds(null)}>
        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
      </IconButton>
    </div>
  );

  const legend = (
    <div className={`pointer-events-auto hidden items-center gap-3 rounded-full px-3 py-1.5 text-[11.5px] text-neutral-500 sm:flex ${FLOATING}`}>
      <span className="flex items-center gap-1.5">
        <NodeSwatch type="stakeholder" />
        People
      </span>
      <span className="flex items-center gap-1.5">
        <NodeSwatch type="organization" />
        Organizations
      </span>
      <span className="h-3 w-px bg-neutral-200" aria-hidden="true" />
      <span className="flex items-center gap-1" title="Org – Person">
        <TieSwatch type="os" />
        Org–Person
      </span>
      <span className="flex items-center gap-1" title="Org – Org">
        <TieSwatch type="oo" />
        Org–Org
      </span>
      <span className="flex items-center gap-1" title="Person – Person">
        <TieSwatch type="ss" />
        Person–Person
      </span>
      {canvasLeads.length > 0 && (
        <span className="flex items-center gap-1" title="Pending AI leads">
          <TieSwatch type="os" dashed className="grayscale" />
          AI lead
        </span>
      )}
    </div>
  );

  const lensHint =
    lens === 'focus' && egoId ? (
      <span>
        Focus · <span className="text-ink">{view.nodesById.get(egoId)?.label}</span>
      </span>
    ) : lens === 'clusters' ? (
      <span>
        {clusterCount} {clusterCount === 1 ? 'cluster' : 'clusters'} · {modularityLabel(analysis.modularity).toLowerCase()} clustering
      </span>
    ) : null;

  const canvas = graph && view.nodes.length > 0 && (
    <NetworkCanvas
      ref={canvasRef}
      nodes={view.nodes}
      links={view.links}
      analysis={analysis}
      intervals={intervals}
      time={timeOn ? time : null}
      lens={lens}
      egoId={egoId}
      selectedId={selectedId}
      compareId={compareId}
      hoverId={hoverId}
      highlightIds={highlightIds}
      highlightCluster={highlightCluster}
      paths={paths}
      activePath={activePath}
      leads={canvasLeads}
      leadFocusId={leadFocusId}
      solidifying={solidifying}
      picked={picked}
      pickMode={pickMode}
      clusterNames={names}
      showLabels={showLabels}
      onToggleLabels={() => setShowLabels(!showLabels)}
      reducedMotion={reducedMotion}
      roleLineOf={lineOf}
      onNodeClick={handleNodeClick}
      onNodeDoubleClick={openProfile}
      onLinkClick={setEditLink}
      onBackgroundClick={() => {
        if (pickMode) return;
        setSelectedId(null);
        setPathEnds(null);
        setCompareId(null);
        if (!isDesktop) setSheetSnap('peek');
      }}
      compactControls={!isDesktop}
      insetBottom={isDesktop ? 0 : sheetSnap === 'half' ? Math.round(mainHeight * 0.52) : 104}
    />
  );

  const rightShows: 'ask' | 'dossier' = askOpen && (rightTab === 'ask' || !selectedNode) ? 'ask' : 'dossier';

  return (
    <div ref={workspaceRef} className={WORKSPACE_CLASS} style={WORKSPACE_STYLE}>
      <style>{WORKSPACE_CSS}</style>

      {/* ---------------- Top bar ---------------- */}
      <header className="relative z-30 flex h-14 flex-shrink-0 items-center gap-3 border-b border-neutral-200/70 px-3 sm:px-4">
        <div className="min-w-0 flex-shrink lg:flex-shrink-0">
          <h1 className="flex items-center gap-2 truncate text-[17px] leading-tight tracking-[-0.02em] text-ink sm:text-[18px]">
            Network
            {data.limited && (
              <span
                className="hidden rounded-full px-2 text-[11px] leading-5 tracking-normal text-neutral-500 ring-1 ring-inset ring-neutral-200 sm:inline"
                title="This server returns the older relationships graph: no roles, dates or influence. Update the server for the full workspace."
              >
                Limited data — update the server
              </span>
            )}
            {data.loading && graph && <Spinner className="h-3 w-3 text-neutral-400" />}
          </h1>
          <p className="truncate text-[12px] tabular-nums text-neutral-500">{graph ? meta : 'Loading'}</p>
        </div>

        {isDesktop && <div className="ml-2">{lensControl(false)}</div>}

        {/* Phones: the buttons keep their width and the title's meta line truncates instead */}
        <div className="ml-auto flex min-w-0 flex-shrink-0 items-center gap-1 lg:flex-shrink">
          {isDesktop && graph && (
            <EntityPicker
              ref={searchRef}
              nodes={view.nodes}
              lineOf={lineOf}
              onPick={(id) => select(id)}
              onHover={setHoverId}
              placeholder="Search"
              ariaLabel="Search entities (/)"
              className="w-[140px] 2xl:w-[240px]"
            />
          )}
          {!isDesktop && toolButton('Search', <Search className="h-4 w-4" strokeWidth={1.75} />, () => setSearchOpen((s) => !s), searchOpen)}
          <div ref={filtersRef} className="relative">
            {toolButton('Filters', <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />, () => setFiltersOpen((o) => !o), filtersOpen, !graph, filterCount)}
            {isDesktop && filtersOpen && (
              <div className={`absolute right-0 top-10 z-40 w-[300px] animate-fade-up rounded-2xl p-2 py-3 ${FLOATING}`}>{filtersPanel}</div>
            )}
          </div>
          {toolButton(timeOn ? 'Hide time' : hasDates ? 'Time' : 'Time — no dated ties', <Clock3 className="h-4 w-4" strokeWidth={1.75} />, toggleTime, timeOn, !hasDates)}
          {isDesktop ? (
            <>
              {toolButton(pickMode === 'path' ? 'Cancel path' : 'Find path', <Route className="h-4 w-4" strokeWidth={1.75} />, () => startPick('path'), pickMode === 'path', !graph)}
              {toolButton(pickMode === 'link' ? 'Cancel link' : 'Add relationship', <Plus className="h-4 w-4" strokeWidth={1.75} />, () => startPick('link'), pickMode === 'link', !graph)}
              <span className="mx-1 h-5 w-px bg-neutral-200" aria-hidden="true" />
              <button
                type="button"
                onClick={() => openAsk()}
                aria-label="Ask the network (⌘K)"
                className={`inline-flex h-9 w-[200px] items-center gap-2 rounded-full pl-3 pr-1.5 text-left text-[13px] transition-colors 2xl:w-[260px] ${
                  askOpen ? 'bg-primary-50 text-primary-700' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200/70'
                }`}
              >
                <Sparkles className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
                <span className="min-w-0 flex-1 truncate">Ask the network…</span>
                <kbd className="inline-flex h-6 items-center gap-0.5 rounded-full bg-white px-1.5 font-ui text-[11px] text-neutral-500 ring-1 ring-inset ring-neutral-200">
                  <Command className="h-3 w-3" strokeWidth={1.75} />K
                </kbd>
              </button>
            </>
          ) : (
            <>
              <Menu
                items={[
                  { label: pickMode === 'path' ? 'Cancel path' : 'Find path', icon: Route, onSelect: () => startPick('path') },
                  { label: pickMode === 'link' ? 'Cancel link' : 'Add relationship', icon: Plus, onSelect: () => startPick('link') },
                  { label: showLabels ? 'Hide labels' : 'Show labels', icon: Tag, onSelect: () => setShowLabels(!showLabels) },
                ]}
                trigger={(p) => (
                  <IconButton label="More" size="sm" {...p}>
                    <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                )}
              />
              <IconButton label="Ask the network" size="sm" onClick={() => openAsk()} className="!text-primary-600">
                <Sparkles className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
            </>
          )}
        </div>
      </header>

      {!isDesktop && searchOpen && graph && (
        // above the bottom sheet (z-30), so the results list is not hidden under it
        <div className="relative z-40 flex-shrink-0 border-b border-neutral-200/70 px-3 py-2">
          <EntityPicker
            ref={searchRef}
            autoFocus
            nodes={view.nodes}
            lineOf={lineOf}
            onPick={(id) => {
              select(id);
              setSearchOpen(false);
            }}
            onEscape={() => setSearchOpen(false)}
            placeholder="Search entities"
          />
        </div>
      )}

      <div className="relative flex min-h-0 flex-1">
        {/* ---------------- Briefing rail ---------------- */}
        {isDesktop && graph && leftOpen && (
          <aside aria-label="Briefing" className="nw-in-left w-[320px] flex-shrink-0 overflow-y-auto overscroll-contain border-r border-neutral-200/70">
            {briefing(true)}
          </aside>
        )}
        {isDesktop && graph && !leftOpen && (
          <div className="flex w-11 flex-shrink-0 flex-col items-center border-r border-neutral-200/70 py-2">
            <IconButton
              label="Show briefing"
              size="sm"
              onClick={() => {
                autoCollapsed.current = false;
                setLeftOpen(true);
              }}
            >
              <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
            <IconButton
              label="Briefing"
              size="sm"
              onClick={() => {
                autoCollapsed.current = false;
                setLeftOpen(true);
              }}
              className="mt-1"
            >
              <FileText className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
        )}

        {/* ---------------- Canvas ---------------- */}
        <main ref={mainRef} className="relative min-w-0 flex-1 overflow-hidden" aria-label="Network map">
          {loading ? (
            <div className="flex h-full items-center justify-center gap-2 text-[13px] text-neutral-400">
              <Spinner /> Loading network
            </div>
          ) : data.error && !graph ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{data.error}</p>
              <Button size="sm" variant="secondary" onClick={() => data.reload()}>
                Retry
              </Button>
            </div>
          ) : graph && graph.nodes.length === 0 ? (
            <EmptyState
              icon={Waypoints}
              title="No relationships yet"
              className="h-full"
              action={
                <Button size="sm" variant="primary" onClick={() => navigate('/stakeholders')}>
                  Add relationships
                </Button>
              }
            />
          ) : graph && view.nodes.length === 0 ? (
            <EmptyState
              icon={SlidersHorizontal}
              title="Nothing matches the filters"
              className="h-full"
              action={
                <Button size="sm" variant="secondary" onClick={() => setFilters(DEFAULT_FILTERS)}>
                  Reset filters
                </Button>
              }
            />
          ) : (
            canvas
          )}

          {graph && view.nodes.length > 0 && (
            <>
              {/* top overlays */}
              <div className="pointer-events-none absolute inset-x-3 top-3 z-20 flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col items-start gap-2">
                  {!isDesktop && <div className="pointer-events-auto">{lensControl(true)}</div>}
                  {lensHint && isDesktop && (
                    <p className="rounded-full bg-white/90 px-2.5 py-1 text-[12px] text-neutral-500 ring-1 ring-black/[0.04] backdrop-blur">{lensHint}</p>
                  )}
                </div>
                <div className="flex min-w-0 flex-col items-end gap-2">{pathChip}</div>
              </div>

              {/* bottom overlays */}
              <div
                className="pointer-events-none absolute inset-x-3 z-20 flex flex-col items-center gap-2"
                style={{ bottom: isDesktop ? 12 : 116 }}
              >
                {pickBanner}
                {notice && (
                  <p role="status" className={`animate-fade-up rounded-full px-3.5 py-1.5 text-[12.5px] text-ink ${FLOATING}`}>
                    {notice}
                  </p>
                )}
                {timeOn && extent && (
                  <div className="pointer-events-auto w-full max-w-[620px] pr-12 sm:pr-14 lg:pr-12">
                    <TimeScrubber
                      extent={extent}
                      value={Math.min(extent.max, Math.max(extent.min, time))}
                      onChange={setTime}
                      playing={playing}
                      onPlayingChange={setPlaying}
                      histogram={histogram}
                      activeTies={activeLinks.length}
                      onClose={toggleTime}
                      reducedMotion={reducedMotion}
                    />
                  </div>
                )}
              </div>
              {isDesktop && !timeOn && !pickMode && !notice && (
                <div className="pointer-events-none absolute bottom-3 left-3 z-10 animate-fade-up motion-reduce:animate-none">{legend}</div>
              )}
            </>
          )}

          {!isDesktop && graph && (
            <MobileSheet<MobileTab>
              tabs={[
                { value: 'briefing', label: 'Briefing' },
                {
                  value: 'dossier',
                  label: selectedNode ? <span className="max-w-[120px] truncate">{selectedNode.label}</span> : 'Dossier',
                  disabled: !selectedNode,
                },
                {
                  value: 'ask',
                  label: (
                    <>
                      <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
                      Ask
                    </>
                  ),
                },
              ]}
              tab={mobileTab === 'dossier' && !selectedNode ? 'briefing' : mobileTab}
              onTab={setMobileTab}
              snap={sheetSnap}
              onSnap={setSheetSnap}
              containerHeight={mainHeight}
              fillContent={mobileTab === 'ask'}
              peek={
                selectedNode
                  ? `${selectedNode.label} · ${lineOf(selectedNode)}`
                  : `Top: ${view.nodesById.get(analysis.ranked.influence[0])?.label || '—'} · ${clusterCount} clusters · ${vulns.gatekeepers.length} ${
                      vulns.gatekeepers.length === 1 ? 'gatekeeper' : 'gatekeepers'
                    }`
              }
            >
              {mobileTab === 'ask' ? askPanel() : mobileTab === 'dossier' && selectedNode ? dossier : briefing(false)}
            </MobileSheet>
          )}
        </main>

        {/* ---------------- Dossier / Ask ---------------- */}
        {rightOpen && (
          <aside
            aria-label={rightShows === 'ask' ? 'Ask' : 'Dossier'}
            className="flex w-[360px] flex-shrink-0 flex-col border-l border-neutral-200/70 bg-white"
          >
            {askOpen && selectedNode && (
              <div role="tablist" aria-label="Right panel" className="flex flex-shrink-0 items-center gap-1 border-b border-neutral-200/70 px-2 py-1.5">
                <button
                  type="button"
                  role="tab"
                  aria-selected={rightShows === 'dossier'}
                  onClick={() => setRightTab('dossier')}
                  className={`inline-flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors ${
                    rightShows === 'dossier' ? 'bg-neutral-100 font-medium text-ink' : 'text-neutral-500 hover:text-ink'
                  }`}
                >
                  <span className="truncate">{selectedNode.label}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={rightShows === 'ask'}
                  onClick={() => setRightTab('ask')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors ${
                    rightShows === 'ask' ? 'bg-neutral-100 font-medium text-ink' : 'text-neutral-500 hover:text-ink'
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
                  Ask
                </button>
              </div>
            )}
            <div key={rightShows === 'ask' ? 'ask' : selectedNode?.id} className="nw-in-right flex min-h-0 flex-1 flex-col">
              {rightShows === 'ask' ? (
                askPanel(() => {
                  setAskOpen(false);
                  setHighlightIds([]);
                })
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{dossier}</div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* ---------------- Dialogs ---------------- */}
      <Dialog open={!isDesktop && filtersOpen} onClose={() => setFiltersOpen(false)} label="Filters" handle className="max-h-[85dvh] sm:max-w-sm">
        <DialogHeader title="Filters" onClose={() => setFiltersOpen(false)} />
        <DialogBody className="!px-3">
          <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>{filtersPanel}</div>
        </DialogBody>
      </Dialog>

      <CreateLinkDialog
        a={linkPair ? view.nodesById.get(linkPair[0]) || null : null}
        b={linkPair ? view.nodesById.get(linkPair[1]) || null : null}
        categories={categories}
        onClose={() => setLinkPair(null)}
        onCreated={() => {
          setLinkPair(null);
          flash('Relationship created');
          data.reload(true);
        }}
      />
      <AddToDirectoryDialog
        lead={adding}
        onClose={() => setAdding(null)}
        onAccept={onAccept}
        onCreated={({ label, accepted }) => {
          setAdding(null);
          flash(accepted ? `${label} added, and the connection recorded` : `${label} added to the directory`);
          data.reload(true);
        }}
      />
      <EditLinkDialog
        link={editLink}
        nodesById={view.nodesById}
        categories={categories}
        onClose={() => setEditLink(null)}
        onChanged={() => {
          setEditLink(null);
          data.reload(true);
        }}
      />
    </div>
  );
};

export default NetworkWorkspace;
